/**
 * Photo Gallery Component - Simplified for quick restoration
 */

import React from 'react';
import { Eye } from 'lucide-react';
import type { Photo, PhotoAlbum } from '@/types/gallery';

interface PhotoGalleryProps {
  photos: Photo[];
  album?: PhotoAlbum;
  className?: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  photos,
  album,
  className = ''
}) => {
  if (photos.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-gray-400 mb-4">
          <Eye className="h-12 w-12 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No photos</h3>
        <p className="text-gray-600">
          {album ? `${album.title} doesn't have any photos yet.` : 'No photos to display.'}
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {album && (
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">{album.title}</h2>
          {album.description && (
            <p className="text-gray-600 max-w-2xl mx-auto">{album.description}</p>
          )}
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <span>{photos.length} photos</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-4">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="group relative overflow-hidden rounded-lg bg-gray-100 w-full md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.67rem)]"
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={photo.url}
                alt={photo.caption || `Photo ${photo.id}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-sm font-medium line-clamp-2">
                  {photo.caption}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PhotoGallery;

